import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRolePermissions } from '../contexts/RolePermissionsContext';
import OnboardingWizard from '../components/OnboardingWizard';
import SetupChecklist from '../components/SetupChecklist';
import { 
  TrendingUp, BarChart3, Car, Users, 
  Mail, Truck, CreditCard, Wallet, Wrench, 
  PieChart, FileText, Building2, Facebook, LogOut, DollarSign,
  Settings, Zap, Calendar, Shield, MessageSquare, ClipboardCheck, Crown, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LauncherPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { canViewPage, loading: permissionsLoading } = useRolePermissions();
  const [badges, setBadges] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Marketing only users
  const MARKETING_ONLY_USERS = ['maxime@glive.ca'];
  const isMarketingOnly = MARKETING_ONLY_USERS.includes(user?.email?.toLowerCase());
  
  // Role checks
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isManager = user?.role === 'manager';
  const isAccountant = user?.role === 'accountant';
  const isUserRole = user?.role === 'user';

  // Fetch notification badges
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/launcher/badges`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setBadges(data);
        }
      } catch (error) {
        console.error('Error fetching badges:', error);
      }
    };
    
    fetchBadges();
    // Check onboarding status for admin users
    if (isAdmin && user?.dealer_id) {
      fetch(`${API_URL}/api/signup/dealers/${user.dealer_id}/onboarding-status`)
        .then(r => r.json())
        .then(data => { if (!data.onboarding_completed) setShowOnboarding(true); })
        .catch(() => {});
    }
    // Trigger entrance animation
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  // Define all sidebar apps - clean monochrome style (simplified for corner dealers)
  const allApps = [
    // REMOVED: président - not needed for corner dealers
    // REMOVED: dashboard - not needed for corner dealers
    { id: 'sales', name: 'Ventes', icon: BarChart3, path: '/sales-dashboard' },
    { id: 'inventory', name: 'Inventaire', icon: Car, path: '/inventory' },
    { id: 'crm', name: 'CRM', icon: Users, path: '/marketing' },
    { id: 'evaluations', name: 'Évaluations', icon: Mail, path: '/appraisals' },
    { id: 'wholesale', name: 'Wholesale', icon: Truck, path: '/wholesale-contracts' },
    { id: 'accounting', name: 'Comptabilité', icon: FileText, path: '/accounting', highlight: 'emerald' },
    { id: 'depots', name: 'Dépôts', icon: CreditCard, path: '/deposits' },
    // REMOVED: cashflow (Trésorerie) - not needed for corner dealers
    { id: 'recon', name: 'Recon.', icon: Wrench, path: '/recon' },
    { id: 'tableau', name: 'Tableau', icon: BarChart3, path: '/sales-board' },
    // REMOVED: budget - not needed for corner dealers
    // REMOVED: floorplan - not needed for corner dealers
    { id: 'suppliers', name: 'Fournisseurs', icon: DollarSign, path: '/supplier-payments' },
    // HIDDEN FROM LAUNCHER: reports, users, automations - accessible from Paramètres
    // HIDDEN FROM LAUNCHER: messages, today - accessible from CRM
    { id: 'settings', name: 'Paramètres', icon: Settings, path: '/settings', adminOnly: true },
    { id: 'super-admin', name: 'Admin Plateforme', icon: Shield, path: '/super-admin', ownerOnly: true },
    { id: 'billing', name: 'Facturation', icon: CreditCard, path: '/billing', adminOnly: true },
    { id: 'subscription', name: 'Mon Abonnement', icon: Crown, path: '/subscription', adminOnly: true },
    // REMOVED: dealers - not needed for corner dealers
    { id: 'fb-marketplace', name: 'FB Marketplace', icon: Facebook, path: '/fb-marketplace', hideForRoles: ['accountant'] },
    { id: 'dealer-website', name: 'Site Web', icon: Globe, path: '/dealer-website', hideForRoles: ['accountant'] },
  ];

  // Filter apps based on dynamic role permissions
  const visibleApps = useMemo(() => {
    if (isMarketingOnly) {
      return allApps.filter(app => ['crm', 'tableau', 'flowboard'].includes(app.id));
    }
    
    // Use dynamic permissions from RolePermissionsContext only
    return allApps.filter(app => {
      if (app.hideForRoles && app.hideForRoles.includes(user?.role)) return false;
      // Owner-only: restricted to a single platform owner email
      if (app.ownerOnly) return user?.email === 'ntoraubally@entrepotautomontreal.ca';
      // Admin-only pages bypass permission check
      if (app.adminOnly) return isAdmin || isSuperAdmin;
      return canViewPage(app.id);
    });
  }, [isMarketingOnly, canViewPage, user?.role, isAdmin, isSuperAdmin]);

  // Get current time greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  // Get current date
  const getCurrentDate = () => {
    return new Date().toLocaleDateString('fr-CA', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" data-testid="launcher-page">
      {showOnboarding && <OnboardingWizard onComplete={() => setShowOnboarding(false)} />}
      {/* Status Bar Style Header */}
      <div className={`pt-safe-top transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <div className="flex justify-between items-center px-6 py-3 text-xs text-slate-400">
          <span>{new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</span>
          <div className="flex items-center gap-2">
            <span className="text-green-400">●</span>
            <span>En ligne</span>
          </div>
        </div>
      </div>

      {/* Greeting Section */}
      <div className={`px-6 pt-8 pb-6 transition-all duration-700 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <p className="text-slate-400 text-sm capitalize">{getCurrentDate()}</p>
        <h1 className="text-3xl font-semibold mt-1">
          {getGreeting()}, <span className="text-blue-400">{user?.name?.split(' ')[0] || 'Utilisateur'}</span>
        </h1>
      </div>

      {/* Setup Progress Checklist */}
      <SetupChecklist />

      {/* Apps Grid - Enhanced icons with subtle glow and badges */}
      <div className="px-8 pb-32">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-6 sm:gap-8">
          {visibleApps.map((app, index) => {
            const IconComponent = app.icon;
            const badgeCount = badges[app.id];
            return (
              <button
                key={app.id}
                onClick={() => navigate(app.path)}
                className={`flex flex-col items-center gap-2 p-2 transition-all duration-500 active:scale-95 group ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${150 + index * 50}ms` }}
                data-testid={`app-${app.id}`}
              >
                <div className="relative">
                  {/* iPhone-style liquid button - smaller and more subtle */}
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-[26%] transition-all duration-500 group-hover:scale-105 group-active:scale-95 backdrop-blur-lg shadow-lg group-hover:shadow-xl ${
                    app.highlight === 'emerald' 
                      ? 'bg-gradient-to-br from-emerald-500/70 via-emerald-600/60 to-emerald-700/70 group-hover:from-emerald-400/80 group-hover:via-emerald-500/70 group-hover:to-emerald-600/80 shadow-emerald-500/20 group-hover:shadow-emerald-400/30' 
                      : 'bg-gradient-to-br from-slate-700/60 via-slate-800/50 to-slate-900/60 group-hover:from-blue-600/70 group-hover:via-blue-700/60 group-hover:to-blue-800/70 shadow-slate-900/30 group-hover:shadow-blue-500/30'
                  } border border-white/8 group-hover:border-white/15`}
                  style={{
                    boxShadow: app.highlight === 'emerald' 
                      ? '0 4px 16px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.08)' 
                      : '0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                  }}
                  >
                    {/* Glass shine effect - more subtle */}
                    <div className="absolute inset-0 rounded-[26%] bg-gradient-to-b from-white/10 via-transparent to-transparent opacity-40 group-hover:opacity-60 transition-opacity duration-500" />
                    
                    <IconComponent className={`w-6 h-6 sm:w-7 sm:h-7 transition-all duration-500 relative z-10 ${
                      app.highlight === 'emerald' 
                        ? 'text-white/90 group-hover:text-white drop-shadow-sm' 
                        : 'text-slate-200/80 group-hover:text-white drop-shadow-sm'
                    }`} strokeWidth={1.5} />
                  </div>
                  {/* Notification Badge - smaller */}
                  {badgeCount && (
                    <div className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1.5 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-md border border-slate-900 animate-pulse">
                      <span className="text-[9px] font-semibold text-white">{badgeCount > 99 ? '99+' : badgeCount}</span>
                    </div>
                  )}
                </div>
                <span className={`text-[11px] sm:text-xs text-center leading-tight transition-all duration-500 font-normal max-w-[70px] ${
                  app.highlight === 'emerald' 
                    ? 'text-emerald-300/90 group-hover:text-emerald-200' 
                    : 'text-slate-300/80 group-hover:text-white'
                }`}>
                  {app.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Logout button - Fixed bottom left - subtle liquid style */}
      <div className={`fixed bottom-8 left-8 transition-all duration-700 delay-700 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
        <button
          onClick={logout}
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-gradient-to-br from-slate-700/60 via-slate-800/50 to-slate-900/60 hover:from-red-600/70 hover:via-red-700/60 hover:to-red-800/70 backdrop-blur-lg border border-white/8 hover:border-red-400/20 transition-all duration-500 active:scale-95 shadow-lg hover:shadow-xl hover:shadow-red-500/20 group"
          style={{
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
          }}
          data-testid="app-logout"
        >
          {/* Glass shine effect */}
          <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-white/8 via-transparent to-transparent opacity-40 group-hover:opacity-60 transition-opacity duration-500" />
          
          <LogOut className="w-4 h-4 text-slate-200/80 group-hover:text-white transition-all duration-500 relative z-10 drop-shadow-sm" strokeWidth={1.5} />
          <span className="text-xs text-slate-200/80 group-hover:text-white transition-all duration-500 font-normal relative z-10">
            Déconnexion
          </span>
        </button>
      </div>

      {/* AutoHub360 Logo - Fixed bottom right */}
      <div className={`fixed bottom-8 right-8 transition-all duration-700 delay-700 ${isLoaded ? 'opacity-60 translate-x-0 hover:opacity-100' : 'opacity-0 translate-x-8'}`}>
        <img 
          src="/logo-autohub360.png" 
          alt="AutoHub360" 
          className="h-16 transition-opacity duration-300"
        />
      </div>
    </div>
  );
};

export default LauncherPage;
